import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { Lightbox } from "@/components/site/Lightbox";
import { useApi, img } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal, ZoomImg } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { SearchBar } from "@/components/site/SearchBar";
import { ListingCard } from "@/components/site/ListingCard";
import { StoryCard } from "@/components/site/StoryCard";
import { DirectoryCard } from "@/pages/Directory";
import { Group, PlaceCard } from "@/pages/Search";
import { GalleryStrip } from "@/components/site/GalleryStrip";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/lib/i18n";

const Hero = ({ p }) => {
  const { t, L } = useLang();
  return (
    <section className="relative h-[52vh] min-h-[360px] overflow-hidden" data-testid="place-hero">
      <ZoomImg src={img(p.cover_image)} alt={`${L(p, "name")} — Escamequita, Nicaragua`} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
      <div className="container-x relative flex h-full flex-col justify-end pb-20">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-sand">{t(p.kind === "activity" ? "place.activity" : "place.area")} · Escamequita</p>
        <h1 className="mt-3 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="place-title">{L(p, "title") || L(p, "name")}</h1>
      </div>
    </section>
  );
};

const PlaceGallery = ({ p }) => {
  const [idx, setIdx] = useState(null);
  const { L } = useLang();
  if (!p.gallery?.length) return null;
  const photos = p.gallery.map((src) => ({ src, alt: `${L(p, "name")} — Escamequita, Nicaragua` }));
  return (
    <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3" data-testid="place-gallery-photos">
      {p.gallery.map((src, i) => (
        <button key={src} type="button" onClick={() => setIdx(i)} className="img-zoom aspect-[4/3] overflow-hidden rounded-2xl" data-testid={`place-photo-${i}`}>
          <img src={img(src)} alt={photos[i].alt} loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
      {idx !== null && <Lightbox images={photos} index={idx} onChange={setIdx} onClose={() => setIdx(null)} />}
    </div>
  );
};

const MorePlaces = ({ current }) => {
  const { t } = useLang();
  const { data } = useApi(`/places?kind=${current.kind}`, []);
  const others = data.filter((p) => p.slug !== current.slug).slice(0, 3);
  return others.length > 0 && <Group title={t("place.more")} testId="place-more">{others.map((p) => <PlaceCard key={p.slug} p={p} />)}</Group>;
};

export default function PlacePage() {
  const { slug } = useParams();
  const { t, L, to } = useLang();
  const { data, loading, error } = useApi(`/places/${slug}`, null);
  if (error) return <NotFound />;
  if (loading || !data) return <div className="min-h-[60vh]" />;
  const { place: p, listings, directory, posts } = data;
  return (
    <>
      <Seo title={L(p, "seo_title") || `${L(p, "name")} | Escamequita, Nicaragua`} description={L(p, "seo_description") || L(p, "intro")} image={p.cover_image} />
      <Hero p={p} />
      <div className="container-x relative z-10 -mt-8 max-w-4xl"><SearchBar compact testId="place-search" /></div>
      <section className="container-x pb-28 pt-12" data-testid={`place-page-${p.slug}`}>
        <Link to={to("/")} className="inline-flex items-center gap-1 text-sm font-bold text-moss hover:text-forest" data-testid="place-back"><ChevronLeft className="h-4 w-4" /> Escamequita</Link>
        <Reveal className="mt-6 max-w-3xl">
          {L(p, "intro") && <p className="text-lg font-semibold leading-relaxed text-ink md:text-xl" data-testid="place-intro">{L(p, "intro")}</p>}
          <div className="rich mt-8" data-testid="place-content"><RichText text={L(p, "content")} /></div>
          <div className="mt-8 flex flex-wrap gap-3">
            {p.cta_url && L(p, "cta_label") && <a href={p.cta_url} target="_blank" rel="noopener" className="btn-primary" data-testid="place-external-cta">{L(p, "cta_label")} <ArrowUpRight className="h-4 w-4" /></a>}
            <Link to={to("/contact")} className={p.cta_url ? "btn-outline" : "btn-primary"} data-testid="place-contact-cta">{t("place.cta", { name: L(p, "name") })}</Link>
          </div>
        </Reveal>
        <PlaceGallery p={p} />
        {p.slug === "big-sky-ranch" && <GalleryStrip className="mt-14" testId="place-gallery" />}
        {listings.length > 0 && <Group title={t("place.homes")} testId="place-listings">{listings.map((l) => <ListingCard key={l.slug} listing={l} />)}</Group>}
        {directory.length > 0 && <Group title={t("place.dir")} testId="place-directory">{directory.map((d) => <DirectoryCard key={d.id} d={d} />)}</Group>}
        {posts.length > 0 && <Group title={t("place.posts")} testId="place-posts">{posts.map((s) => <StoryCard key={s.slug} post={s} />)}</Group>}
        <MorePlaces current={p} />
      </section>
    </>
  );
}
